import { useState } from 'react';
import type { ReactElement } from 'react';
import { ChevronRight } from 'lucide-react';
import type { AdminUserDetail, UserRole, UserTier } from '@xdocs/schemas';
import { Input, Select, useToast, cn } from '@xdocs/ui';
import {
  AdminConfirmDialog,
  TypedConfirmDialog,
  useAdminT,
  useAdminUserMutations,
  useImpersonation,
} from '../../../../features/admin/index.js';
import styles from './ProfileTab.module.css';

export interface ProfileTabProps {
  detail: AdminUserDetail;
}

type OpenDialog = 'role' | 'tier' | 'credits' | 'forceLogout' | 'status' | 'impersonate' | null;

const ROLES: readonly UserRole[] = ['user', 'moderator', 'admin'];
const TIERS: readonly UserTier[] = ['free', 'pro'];

/** F54 "Sensitive actions · confirm + reason → audited" grid
 * (design/app/40-admin-user-detail.html's `userActions`). Ships 6 of the
 * mockup's 7 cards — "Resend verification" is omitted (see this task's
 * returned OPEN ISSUES: no `admin.users.resendVerify` op exists yet in the
 * frozen `op-contracts.ts`, and this task ships no dead/fake-success
 * controls). */
export function ProfileTab({ detail }: ProfileTabProps): ReactElement {
  const { t } = useAdminT();
  const toast = useToast();
  const mutations = useAdminUserMutations();
  const impersonation = useImpersonation();

  const [open, setOpen] = useState<OpenDialog>(null);
  const [role, setRole] = useState<UserRole>(detail.user.role);
  const [tier, setTier] = useState<UserTier>(detail.user.tier);
  const [creditsDelta, setCreditsDelta] = useState('');
  const [creditsFieldError, setCreditsFieldError] = useState<string | null>(null);

  const { user } = detail;
  const isSuspended = user.status === 'suspended';

  function close(): void {
    setOpen(null);
    setCreditsFieldError(null);
  }

  async function handleSetRole(reason: string): Promise<void> {
    await mutations.setRole.mutate(user.id, role, reason);
    toast.push({ tone: 'success', message: t('admin:userDetail.toast.roleUpdated', { email: user.email }) });
    close();
  }

  async function handleSetTier(reason: string): Promise<void> {
    await mutations.setTier.mutate(user.id, tier, reason);
    toast.push({ tone: 'success', message: t('admin:userDetail.toast.tierUpdated', { email: user.email }) });
    close();
  }

  async function handleAdjustCredits(reason: string): Promise<void> {
    const delta = Number(creditsDelta);
    if (!Number.isInteger(delta) || delta === 0) {
      setCreditsFieldError(t('admin:userDetail.confirm.creditsDeltaRequired'));
      return;
    }
    setCreditsFieldError(null);
    await mutations.adjustCredits.mutate(user.id, delta, reason);
    toast.push({ tone: 'success', message: t('admin:userDetail.toast.creditsAdjusted', { email: user.email }) });
    setCreditsDelta('');
    close();
  }

  async function handleForceLogout(reason: string): Promise<void> {
    await mutations.forceLogout.mutate(user.id, reason);
    toast.push({ tone: 'success', message: t('admin:userDetail.toast.sessionsRevoked', { email: user.email }) });
    close();
  }

  async function handleSetStatus(reason: string): Promise<void> {
    const nextStatus = isSuspended ? 'active' : 'suspended';
    await mutations.setStatus.mutate(user.id, nextStatus, reason);
    toast.push({
      tone: 'success',
      message: isSuspended
        ? t('admin:userDetail.toast.reinstated', { email: user.email })
        : t('admin:userDetail.toast.suspended', { email: user.email }),
    });
    close();
  }

  async function handleImpersonate(reason: string): Promise<void> {
    // NOTE: `impersonation.start` only opens the audited, TTL-bound
    // bookkeeping session that drives the banner/countdown — it does NOT
    // (yet) swap the live WS/auth identity to the target user (see the
    // "OPEN ISSUE" doc comment on `ImpersonationContext.tsx` and
    // `features/admin/WIRING.md` §3). Do not navigate away or claim the
    // operator is now "in" the target's session — that would be misleading
    // until the identity swap actually lands.
    await impersonation.start(user.id, user.displayName ?? user.email, reason);
    toast.push({ tone: 'info', message: t('admin:impersonation.toast.entered', { name: user.displayName ?? user.email }) });
    close();
  }

  const actions: { key: Exclude<OpenDialog, null>; label: string; sub: string; danger?: boolean }[] = [
    { key: 'role', label: t('admin:userDetail.action.setRole.label'), sub: t('admin:userDetail.action.setRole.sub') },
    { key: 'tier', label: t('admin:userDetail.action.changeTier.label'), sub: t('admin:userDetail.action.changeTier.sub') },
    { key: 'credits', label: t('admin:userDetail.action.adjustCredits.label'), sub: t('admin:userDetail.action.adjustCredits.sub') },
    { key: 'forceLogout', label: t('admin:userDetail.action.forceLogout.label'), sub: t('admin:userDetail.action.forceLogout.sub') },
    {
      key: 'status',
      label: isSuspended ? t('admin:userDetail.action.reinstate.label') : t('admin:userDetail.action.suspend.label'),
      sub: t('admin:userDetail.action.suspend.sub'),
      danger: !isSuspended,
    },
    { key: 'impersonate', label: t('admin:userDetail.action.impersonate.label'), sub: t('admin:userDetail.action.impersonate.sub') },
  ];

  return (
    <div>
      <div className={cn(styles['eyebrow'])}>{t('admin:userDetail.profile.eyebrow')}</div>
      <div className={cn(styles['grid'])}>
        {actions.map((action) => (
          <button key={action.key} type="button" className={cn(styles['action'], action.danger && styles['danger'])} onClick={() => setOpen(action.key)}>
            <span>
              <span className={cn(styles['actionLabel'])}>{action.label}</span>
              <span className={cn(styles['actionSub'])}>{action.sub}</span>
            </span>
            <ChevronRight size={16} aria-hidden="true" className={cn(styles['chevron'])} />
          </button>
        ))}
      </div>

      <AdminConfirmDialog
        open={open === 'role'}
        onOpenChange={(next) => !next && close()}
        title={t('admin:userDetail.confirm.setRole.title', { email: user.email })}
        body={t('admin:userDetail.confirm.setRole.body')}
        confirmLabel={t('admin:userDetail.confirm.confirm')}
        loading={mutations.setRole.isPending}
        error={mutations.setRole.error?.message}
        onConfirm={handleSetRole}
      >
        <Select
          label={t('admin:userDetail.confirm.roleSelectLabel')}
          value={role}
          onChange={(event) => setRole(event.target.value as UserRole)}
          options={ROLES.map((r) => ({ value: r, label: t(`admin:role.${r}`) }))}
        />
      </AdminConfirmDialog>

      <AdminConfirmDialog
        open={open === 'tier'}
        onOpenChange={(next) => !next && close()}
        title={t('admin:userDetail.confirm.changeTier.title', { email: user.email })}
        body={t('admin:userDetail.confirm.changeTier.body')}
        confirmLabel={t('admin:userDetail.confirm.confirm')}
        loading={mutations.setTier.isPending}
        error={mutations.setTier.error?.message}
        onConfirm={handleSetTier}
      >
        <Select
          label={t('admin:userDetail.confirm.tierSelectLabel')}
          value={tier}
          onChange={(event) => setTier(event.target.value as UserTier)}
          options={TIERS.map((tr) => ({ value: tr, label: t(`admin:tier.${tr}`) }))}
        />
      </AdminConfirmDialog>

      <AdminConfirmDialog
        open={open === 'credits'}
        onOpenChange={(next) => !next && close()}
        title={t('admin:userDetail.confirm.adjustCredits.title', { email: user.email })}
        body={t('admin:userDetail.confirm.adjustCredits.body', { balance: user.creditsBalance })}
        confirmLabel={t('admin:userDetail.confirm.confirm')}
        loading={mutations.adjustCredits.isPending}
        error={mutations.adjustCredits.error?.message ?? creditsFieldError ?? undefined}
        onConfirm={handleAdjustCredits}
      >
        <Input
          type="number"
          label={t('admin:userDetail.confirm.creditsDeltaLabel')}
          value={creditsDelta}
          onChange={(event) => setCreditsDelta(event.target.value)}
        />
      </AdminConfirmDialog>

      <AdminConfirmDialog
        open={open === 'forceLogout'}
        onOpenChange={(next) => !next && close()}
        title={t('admin:userDetail.confirm.forceLogout.title', { email: user.email })}
        body={t('admin:userDetail.confirm.forceLogout.body')}
        confirmLabel={t('admin:userDetail.action.forceLogout.label')}
        destructive
        loading={mutations.forceLogout.isPending}
        error={mutations.forceLogout.error?.message}
        onConfirm={handleForceLogout}
      />

      <AdminConfirmDialog
        open={open === 'status'}
        onOpenChange={(next) => !next && close()}
        title={isSuspended ? t('admin:userDetail.confirm.reinstate.title', { email: user.email }) : t('admin:userDetail.confirm.suspend.title', { email: user.email })}
        body={isSuspended ? t('admin:userDetail.confirm.reinstate.body') : t('admin:userDetail.confirm.suspend.body')}
        confirmLabel={isSuspended ? t('admin:userDetail.action.reinstate.label') : t('admin:userDetail.action.suspend.label')}
        destructive={!isSuspended}
        loading={mutations.setStatus.isPending}
        error={mutations.setStatus.error?.message}
        onConfirm={handleSetStatus}
      />

      <TypedConfirmDialog
        open={open === 'impersonate'}
        onOpenChange={(next) => !next && close()}
        title={t('admin:impersonation.confirm.title', { email: user.email })}
        body={t('admin:impersonation.confirm.body')}
        confirmText={user.email}
        typedFieldLabel={t('admin:impersonation.confirm.typeToConfirmLabel')}
        typedFieldHint={t('admin:impersonation.confirm.typeToConfirmHint', { email: user.email })}
        confirmLabel={t('admin:impersonation.confirm.confirmLabel')}
        loading={impersonation.isStarting}
        error={impersonation.error?.message}
        onConfirm={handleImpersonate}
      />
    </div>
  );
}
